import React, { useEffect, useId, useRef } from 'react';
import { Datepicker as FlowbiteDatepicker } from 'flowbite-datepicker';

/**
 * 공통 날짜 선택 컴포넌트 (flowbite-datepicker 기반)
 * @param {string} value - 표시/선택된 날짜 문자열 (예: 'yyyy-mm-dd')
 * @param {function(string)} onChange - 날짜 선택 시 호출 (날짜 문자열 전달)
 * @param {string} [placeholder='날짜 선택'] - placeholder
 * @param {string} [id] - input id (미지정 시 자동 생성)
 * @param {string} [className] - 래퍼 div className
 * @param {string} [inputClassName=''] - input className
 * @param {string} [format='yyyy-mm-dd'] - 날짜 형식
 * @param {boolean} [autohide=true] - 선택 후 팝업 자동 숨김
 * @param {boolean} [readOnly=true] - 키보드 입력 방지(캘린더만 사용)
 * @param {boolean} [showIcon=true] - 캘린더 아이콘 표시
 */
function Datepicker(props) {
  const {
    value = '',
    onChange,
    placeholder = '날짜 선택',
    id: idProp,
    className = '',
    inputClassName = '',
    iconClassName = '',
    format = 'yyyy-mm-dd',
    autohide = true,
    readOnly = true,
    showIcon = true,
  } = props;

  const generatedId = useId();
  const id = idProp ?? `datepicker-${generatedId.replace(/:/g, '')}`;
  const inputRef = useRef(null);
  const instanceRef = useRef(null);

  useEffect(() => {
    const el = inputRef.current;
    if (!el) return;

    // 1. 인스턴스 생성
    const instance = new FlowbiteDatepicker(el, {
      autohide,
      format,
      defaultViewDate: value || new Date(),
    });
    instanceRef.current = instance;

    // 2. Flowbite 전용 날짜 변경 이벤트 리스너
    const handleChangeDate = (e) => {
      // e.detail.datepicker.getDate('yyyy-mm-dd') 형태로 포맷된 문자열 획득 가능
      const selectedDate = el.value;
      if (value !== selectedDate) {
        onChange?.(selectedDate);
      }
    };

    el.addEventListener('changeDate', handleChangeDate);

    return () => {
      el.removeEventListener('changeDate', handleChangeDate);
      if (instanceRef.current) {
        instance.destroy();
      }
    };
  }, [autohide, format, onChange, value]);

  // 3. 외부(부모)에서 value 변경 시 동기화
  useEffect(() => {
    if (!instanceRef.current || !value) return;

    // 현재 라이브러리가 들고 있는 날짜와 외부 value가 다를 때만 setDate 호출
    const currentDate = inputRef.current.value;
    if (value !== currentDate) {
      instanceRef.current.setDate(value);
    }
  }, [value]);

  return (
    <div className={`group relative ${className}`}>
      <input
        ref={inputRef}
        id={id}
        type="text"
        className={`w-full rounded-lg border border-slate-200 bg-slate-50 px-4 transition-all outline-none placeholder:text-slate-400 ${showIcon ? 'pr-12' : ''} ${inputClassName}`}
        placeholder={placeholder}
        value={value}
        readOnly={readOnly}
        onChange={(e) => onChange?.(e.target.value)}
      />
      {showIcon && (
        <span
          className={`material-symbols-outlined pointer-events-none absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 transition-colors ${iconClassName}`}
          aria-hidden
        >
          calendar_month
        </span>
      )}
    </div>
  );
}

export default Datepicker;
